/**
 * Live league-points projection for an open Bullion+ battle.
 *
 * Mirrors `rankAndSplitLeagueBullionPoints` in `backend/db/leagues/leagueBullionSettleMath.js`:
 * rank by slip score (then potential, then earliest entry). Everyone keeps their slip score;
 * a place-bonus pool is added as if the battle ended now (1–9 WTA, 10–30 top 3, 31+ top 5
 * from real heads). Extra Rubies grow pool size only.
 */
import {
    leagueBullionPointsPoolSize,
    leagueBullionPoolWeight,
    leagueExtraRebuyRubies,
} from "./leagueGameSurfaces";
import type { League, LeaguePlay } from "./types";

export type LeagueBullionScoredPlay = {
    id: number;
    score: number;
    potentialPointsRounded: number;
    createdAt: string | number | Date;
};

export type LeagueBullionPrizePlan =
    | { mode: "winner_take_all" }
    | { mode: "tiered"; shares: number[] };

/** Payout schedule for the full points pool (no rake). */
export function leagueBullionPointsPrizePlan(playerCount: number): LeagueBullionPrizePlan {
    const n = Math.floor(Number(playerCount));
    if (!Number.isFinite(n) || n <= 9) return { mode: "winner_take_all" };
    if (n <= 30) return { mode: "tiered", shares: [0.5, 0.3, 0.2] };
    return { mode: "tiered", shares: [0.38, 0.28, 0.18, 0.1, 0.06] };
}

export function sortLeagueBullionScored<T extends LeagueBullionScoredPlay>(scored: readonly T[]): T[] {
    return [...scored].sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        if (b.potentialPointsRounded !== a.potentialPointsRounded) {
            return b.potentialPointsRounded - a.potentialPointsRounded;
        }
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
}

/** Competition rank: same slip score and same potential share a rank (1, 1, 3, …). */
export function rankLeagueBullionScored<T extends LeagueBullionScoredPlay>(
    scored: readonly T[],
): Array<T & { displayRank: number }> {
    const sorted = sortLeagueBullionScored(scored);
    let lastPts: number | null = null;
    let lastPot: number | null = null;
    let displayRank = 0;
    let position = 0;
    return sorted.map((row) => {
        position += 1;
        if (lastPts === null || row.score !== lastPts || row.potentialPointsRounded !== lastPot) {
            displayRank = position;
            lastPts = row.score;
            lastPot = row.potentialPointsRounded;
        }
        return { ...row, displayRank };
    });
}

function largestRemainderMap(idToFloatAmount: Map<number, number>, targetTotal: number): Map<number, number> {
    const entries = [...idToFloatAmount.entries()]
        .map(([id, v]) => {
            const fv = Number(v);
            const safe = Number.isFinite(fv) && fv > 0 ? fv : 0;
            const floor = Math.floor(safe);
            return { id, floor, rem: safe - floor };
        })
        .filter((e) => e.floor > 0 || e.rem > 0);
    const target = Math.floor(Number(targetTotal));
    if (entries.length === 0) return new Map();

    let sumFloor = entries.reduce((s, e) => s + e.floor, 0);
    let deficit = target - sumFloor;
    if (deficit < 0) {
        entries.sort((a, b) => a.rem - b.rem || a.id - b.id);
        let over = -deficit;
        for (const e of entries) {
            if (over <= 0) break;
            if (e.floor > 0) {
                e.floor -= 1;
                over -= 1;
            }
        }
        sumFloor = entries.reduce((s, e) => s + e.floor, 0);
        deficit = target - sumFloor;
    }
    entries.sort((a, b) => b.rem - a.rem || a.id - b.id);
    const out = new Map<number, number>();
    entries.forEach((e, i) => {
        out.set(e.id, e.floor + (i < deficit ? 1 : 0));
    });
    return out;
}

function normalizePointsSplitArgs(
    rankedSorted: ReadonlyArray<{ id: number }>,
    playerCountOrOpts: number | { playerCount?: number; poolWeight?: number; extraRubies?: number } = rankedSorted.length,
): { playerCount: number; poolWeight: number } {
    const n = rankedSorted.length;
    if (playerCountOrOpts != null && typeof playerCountOrOpts === "object") {
        const playerCountRaw = playerCountOrOpts.playerCount;
        const playerCount = Math.floor(Number(playerCountRaw != null ? playerCountRaw : n));
        const extra = Math.max(0, Math.floor(Number(playerCountOrOpts.extraRubies)) || 0);
        const poolWeightRaw = playerCountOrOpts.poolWeight;
        const poolWeight = Math.floor(
            Number(poolWeightRaw != null ? poolWeightRaw : leagueBullionPoolWeight(playerCount, extra)),
        );
        return {
            playerCount: Number.isFinite(playerCount) ? playerCount : n,
            poolWeight: Number.isFinite(poolWeight) ? poolWeight : n,
        };
    }
    const playerCount = Math.floor(Number(playerCountOrOpts));
    const safe = Number.isFinite(playerCount) ? playerCount : n;
    return { playerCount: safe, poolWeight: safe };
}

/**
 * Split the place-bonus pool by competition rank.
 * Plan uses real player count; pool size uses `poolWeight`.
 * @returns play id → place-bonus share
 */
export function leagueBullionPointsByPlayId(
    rankedSorted: ReadonlyArray<LeagueBullionScoredPlay & { displayRank: number }>,
    playerCountOrOpts: number | { playerCount?: number; poolWeight?: number; extraRubies?: number } = rankedSorted.length,
): Map<number, number> {
    const { playerCount, poolWeight } = normalizePointsSplitArgs(rankedSorted, playerCountOrOpts);
    const prizePool = leagueBullionPointsPoolSize(poolWeight);
    const n = rankedSorted.length;
    const zeroAll = () => new Map(rankedSorted.map((r) => [r.id, 0]));
    if (n === 0 || prizePool <= 0) return zeroAll();

    const plan = leagueBullionPointsPrizePlan(playerCount);
    const floatAmounts = new Map<number, number>();

    if (plan.mode === "winner_take_all") {
        const top = rankedSorted[0];
        const maxScore = top != null ? Number(top.score) : 0;
        const topPot = top != null ? Number(top.potentialPointsRounded) : 0;
        const winners = rankedSorted.filter(
            (p) => Number(p.score) === maxScore && Number(p.potentialPointsRounded) === topPot,
        );
        const nw = winners.length || 1;
        const each = prizePool / nw;
        for (const w of winners) floatAmounts.set(w.id, each);
        const ints = largestRemainderMap(floatAmounts, prizePool);
        const out = zeroAll();
        for (const [id, coins] of ints) out.set(id, coins);
        return out;
    }

    const rawShares = plan.shares;
    const sumShares = rawShares.reduce((a, b) => a + b, 0);
    const shares = sumShares > 0 ? rawShares.map((s) => s / sumShares) : rawShares;
    const k = shares.length;
    const topK = rankedSorted.slice(0, Math.min(k, n));

    const rankToPositions: Record<number, number[]> = {};
    topK.forEach((row, idx) => {
        const pos = idx + 1;
        const r = row.displayRank;
        if (!rankToPositions[r]) rankToPositions[r] = [];
        rankToPositions[r].push(pos);
    });

    const rankToCombined: Record<number, number> = {};
    const rankToCount: Record<number, number> = {};
    for (const rStr of Object.keys(rankToPositions)) {
        const r = Number(rStr);
        const positions = rankToPositions[r]!;
        rankToCombined[r] = positions.reduce((sum, p) => sum + (shares[p - 1] || 0), 0);
        rankToCount[r] = positions.length;
    }

    const topKIds = new Set(topK.map((row) => row.id));
    for (const p of rankedSorted) {
        if (!topKIds.has(p.id)) continue;
        const combined = rankToCombined[p.displayRank];
        const count = rankToCount[p.displayRank] || 1;
        if (combined == null) continue;
        floatAmounts.set(p.id, (prizePool * combined) / count);
    }

    const ints = largestRemainderMap(floatAmounts, prizePool);
    const out = zeroAll();
    for (const [id, coins] of ints) out.set(id, coins);
    return out;
}

export function leagueBullionStandingsPointsByPlayId(
    rankedSorted: ReadonlyArray<LeagueBullionScoredPlay>,
    placeByPlayId: Map<number, number>,
): Map<number, number> {
    const out = new Map<number, number>();
    for (const r of rankedSorted) {
        const slip = Math.max(0, Math.round(Number(r.score) || 0));
        const place = Math.max(0, Math.round(Number(placeByPlayId.get(r.id)) || 0));
        out.set(r.id, slip + place);
    }
    return out;
}

export function rankAndSplitLeagueBullionPoints<T extends LeagueBullionScoredPlay>(
    scored: readonly T[],
    opts: { playerCount?: number; poolWeight?: number; extraRubies?: number } = {},
): {
    ranked: Array<T & { displayRank: number }>;
    pointsByPlayId: Map<number, number>;
    placeByPlayId: Map<number, number>;
    standingsByPlayId: Map<number, number>;
    poolSize: number;
    poolWeight: number;
} {
    const ranked = rankLeagueBullionScored(scored);
    const { playerCount, poolWeight } = normalizePointsSplitArgs(ranked, {
        playerCount: opts.playerCount ?? ranked.length,
        poolWeight: opts.poolWeight,
        extraRubies: opts.extraRubies,
    });
    const poolSize = leagueBullionPointsPoolSize(poolWeight);
    const placeByPlayId = leagueBullionPointsByPlayId(ranked, { playerCount, poolWeight });
    const standingsByPlayId = leagueBullionStandingsPointsByPlayId(ranked, placeByPlayId);
    return { ranked, pointsByPlayId: placeByPlayId, placeByPlayId, standingsByPlayId, poolSize, poolWeight };
}

function openBullionSlip(play: LeaguePlay): { score: number; potential: number } {
    const settlement = play.marketDetails?.settlement;
    const slipRaw =
        settlement?.slipScore != null && Number.isFinite(Number(settlement.slipScore))
            ? Number(settlement.slipScore)
            : Number(settlement?.points);
    const potentialRaw = Number(settlement?.potentialPoints);
    return {
        score: Number.isFinite(slipRaw) ? Math.max(0, Math.round(slipRaw)) : 0,
        potential: Number.isFinite(potentialRaw) ? Math.max(0, Math.round(potentialRaw)) : 0,
    };
}

export type OpenBullionLiveRow = {
    /** Progressive slip score (battle ranking / Bullion tab PTS). */
    slip: number;
    /**
     * Slip + place bonus if the battle ended on the current order.
     * Null until at least one entrant has a slip score.
     */
    ifNow: number | null;
};

type OpenBullionLeague = Pick<League, "plays"> &
    Partial<Pick<League, "prizePotCoins" | "entryFeeRubies">> & {
        members?: Array<{ userId: number }>;
    };

/** Live slip + optional if-now standings (slip + place) for each open Bullion entrant. */
export function openBullionLiveByUserId(league: OpenBullionLeague): Map<number, OpenBullionLiveRow> {
    const open = (league.plays || []).filter((play) => play.game === "bullion" && play.status === "open");
    const out = new Map<number, OpenBullionLiveRow>();
    if (open.length === 0) return out;

    const scored = open.map((play, index) => {
        const slip = openBullionSlip(play);
        return {
            id: index,
            userId: play.userId,
            score: slip.score,
            potentialPointsRounded: slip.potential,
            createdAt: play.placedAt,
        };
    });
    const extraRubies =
        league.members && league.entryFeeRubies != null ? leagueExtraRebuyRubies({
            prizePotCoins: league.prizePotCoins,
            entryFeeRubies: league.entryFeeRubies,
            members: league.members,
        }) : 0;
    const projecting = scored.some((row) => row.score > 0);
    const standingsByPlayId = projecting
        ? rankAndSplitLeagueBullionPoints(scored, { extraRubies }).standingsByPlayId
        : null;
    scored.forEach((play) => {
        const prev = out.get(play.userId);
        const slip = play.score + (prev?.slip ?? 0);
        const share = standingsByPlayId
            ? Math.max(0, Math.round(Number(standingsByPlayId.get(play.id)) || 0))
            : null;
        const ifNow = share == null ? null : share + (prev?.ifNow ?? 0);
        out.set(play.userId, { slip, ifNow });
    });
    return out;
}

export function leagueHasOpenBullionSlipScore(league: OpenBullionLeague): boolean {
    for (const row of openBullionLiveByUserId(league).values()) {
        if (row.slip > 0) return true;
    }
    return false;
}

export function leagueHasOpenBullionBattle(league: Pick<League, "plays">): boolean {
    return (league.plays || []).some((play) => play.game === "bullion" && play.status === "open");
}

/**
 * League points each open Bullion entrant would receive if the battle finished on the current order
 * (slip + place bonus). Keyed by user id. Settled plays are not included.
 *
 * Empty until at least one entrant has a slip score — prematch / all-zero boards
 * must not credit the place bonus as "won" from entry order alone.
 */
export function openBullionFinishPayoutByUserId(league: OpenBullionLeague): Map<number, number> {
    const out = new Map<number, number>();
    for (const [userId, row] of openBullionLiveByUserId(league)) {
        if (row.ifNow == null) continue;
        out.set(userId, row.ifNow);
    }
    return out;
}
